import classNames from "classnames";
import { Calendar, CalendarProps } from "primereact/calendar";
import { FormEvent, Nullable } from "primereact/ts-helpers";
import React, { useMemo } from "react";

import { convertToUTCMidday } from "../../../utils";
import { getFsExcludeClassName } from "../../../utils/getFsExcludeClassName";
import { getInputTestIdByName } from "../../../utils/getInputTestIdByName";
import { WSInputBaseProps } from "../../types/forms";
import { useIsMobile } from "../layout";
import {
  detachFieldProps,
  getFieldAriaProps,
  WSField,
  WSFieldProps
} from "../WSField/WSField";
import {
  WSInputContainer,
  WSInputContainerProps
} from "../WSInputContainer/WSInputContainer";

import { formatDate } from "./utils";
import styles from "./WSInputDate.module.scss";

export type WSInputDateProps = Omit<
  WSInputBaseProps<any, any>,
  "value" | "onChange"
> &
  Omit<WSInputContainerProps, "size" | "align"> &
  WSFieldProps &
  Omit<
    CalendarProps,
    "pt" | "prefix" | "size" | "color" | "selectionMode" | "value" | "onChange"
  > & {
    value: Nullable<Date>;
    onChange(
      value: Nullable<Date>,
      event: FormEvent<Date> | React.ChangeEvent<HTMLInputElement>
    ): void;
  };

const defaultPlaceholder = "MM/DD/YYYY";
const defaultPlaceholderTime = "MM/DD/YYYY HH:mm";

export const WSInputDate = React.forwardRef<HTMLInputElement, WSInputDateProps>(
  (props, ref) => {
    const {
      name,
      status,
      className,
      icon,
      iconRight,
      inputAction,
      disabled,
      value,
      onChange,
      inputClassName,
      placeholder: placeholderProp,
      fsExclude,
      ...otherProps
    } = props;

    const isMobile = useIsMobile();

    const { fieldProps, ...inputProps } = detachFieldProps(otherProps);
    const ariaProps = getFieldAriaProps({
      name,
      label: fieldProps.label,
      helperText: fieldProps.helperText,
      required: fieldProps.required,
      status,
      message: fieldProps.message
    });

    const stringValue = useMemo(() => {
      if (!value) {
        return "";
      }

      return formatDate(value, inputProps.showTime);
    }, [value, inputProps.showTime]);

    const placeholder = useMemo(() => {
      if (placeholderProp) {
        return placeholderProp;
      }

      if (inputProps.showTime) {
        return defaultPlaceholderTime;
      }

      return defaultPlaceholder;
    }, [inputProps.showTime, placeholderProp]);

    return (
      <WSField {...fieldProps} name={name} status={status}>
        <WSInputContainer
          status={status}
          icon={icon}
          iconRight={iconRight}
          inputAction={inputAction}
          disabled={disabled}
          className={className}
        >
          {isMobile ? (
            <input
              name={name}
              type={inputProps.showTime ? "datetime-local" : "date"}
              className={classNames(
                styles.input,
                inputClassName,
                getFsExcludeClassName(fsExclude)
              )}
              value={stringValue}
              data-testid={getInputTestIdByName(name)}
              disabled={disabled}
              {...ariaProps}
              onClick={(event) => {
                // iOS dispatches a synthetic click on the underlying input when the user
                // dismisses the native picker (incl. tapping Clear). Those clicks have
                // detail === 0; only call showPicker for real user taps to avoid
                // re-opening the picker on dismis.
                if (event.detail === 0) return;

                const target = event.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                };
                if (typeof target.showPicker === "function") {
                  try {
                    target.showPicker();
                  } catch {
                    // showPicker requires user activation; ignore if not granted
                  }
                }
              }}
              onChange={(event) => {
                const raw = event.target.value;

                if (!raw) {
                  onChange?.(null, event);
                  return;
                }

                const isoString = inputProps.showTime
                  ? raw
                  : `${raw}T12:00:00Z`;
                const newDate = new Date(isoString);

                if (Number.isNaN(newDate.getTime())) {
                  onChange?.(null, event);
                  return;
                }

                onChange?.(newDate, event);
              }}
            />
          ) : (
            <Calendar
              inputRef={ref}
              name={name}
              className={classNames(
                inputClassName,
                getFsExcludeClassName(fsExclude)
              )}
              placeholder={placeholder}
              value={value}
              disabled={disabled}
              onChange={(e) => {
                let newValue = e.value;

                if (newValue instanceof Date && !inputProps.showTime) {
                  convertToUTCMidday(newValue);
                }

                // Fire the onChange callback with the normalized date(s).
                onChange?.(newValue, e);
              }}
              pt={{
                input: { root: { className: styles.input, ...ariaProps } }
              }}
              data-testid={getInputTestIdByName(name)}
              baseZIndex={1200}
              {...(inputProps as any)}
            />
          )}
        </WSInputContainer>
      </WSField>
    );
  }
);

WSInputDate.displayName = "WSInputDate";
